import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { FiPlus, FiTrash2, FiAlertTriangle, FiSearch, FiEdit2, FiCheck, FiX } from 'react-icons/fi';

export const PRESET_COLORS = [
  '#3B82F6', // Azul
  '#10B981', // Verde Esmeralda
  '#F59E0B', // Âmbar
  '#EF4444', // Vermelho
  '#8B5CF6', // Roxo
  '#EC4899', // Rosa
  '#06B6D4', // Ciano
  '#14B8A6', // Verde Água
  '#F97316', // Laranja
  '#6366F1', // Índigo
  '#84CC16', // Lima
  '#64748B', // Cinza
  '#1E3A8A', // Azul Marinho
  '#820AD1', // Roxo Nubank
];

/**
 * Reusable Color Picker with preset swatches and custom hex picker using Radix Popover Portal
 */
const ColorPicker = ({ value, onChange, testIdPrefix = 'color-picker' }) => {
  const [open, setOpen] = useState(false);
  const currentColor = value || '#3B82F6';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-trigger`}
          className="w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-center p-1.5 hover:scale-105 transition-transform bg-white dark:bg-slate-800 shadow-xs flex-shrink-0 cursor-pointer"
          title="Escolher cor"
        >
          <span
            className="w-full h-full rounded-lg border border-black/10 shadow-xs block"
            style={{ backgroundColor: currentColor }}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="z-[9999] p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl w-60"
      >
        <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">
          Cores do Dashboard
        </p>
        <div className="grid grid-cols-7 gap-1.5 mb-3">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`${testIdPrefix}-preset-${c.replace('#', '')}`}
              onClick={(e) => {
                e.stopPropagation();
                onChange(c);
                setOpen(false);
              }}
              className={`w-6 h-6 rounded-md transition-transform hover:scale-110 border cursor-pointer ${
                currentColor.toLowerCase() === c.toLowerCase()
                  ? 'ring-2 ring-blue-600 ring-offset-1 border-white scale-110'
                  : 'border-black/10'
              }`}
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
        </div>
        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500 dark:text-slate-400">Personalizada:</span>
          <div className="flex items-center gap-1.5">
            <input
              type="color"
              data-testid={`${testIdPrefix}-native`}
              value={currentColor}
              onChange={(e) => onChange(e.target.value)}
              className="w-6 h-6 rounded border-0 p-0 cursor-pointer bg-transparent"
            />
            <span className="text-[10px] font-mono text-slate-600 dark:text-slate-300 uppercase">
              {currentColor}
            </span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Reusable CRUD list dialog with Color selection, Inline Edit (pencil icon), and Delete (trash icon).
 * Used in Carteiras, Registros for Forma de Pagamento, Banco and Categoria management.
 */
export const CrudListDialog = ({
  open,
  onOpenChange,
  title,
  placeholder,
  newValue,
  onNewValueChange,
  onAdd,
  items,
  onUpdate,
  onDelete,
  testIdPrefix,
  defaultColor = '#3B82F6',
  description = 'Adicione, edite e remova itens da lista com cores personalizadas para o dashboard.',
  confirmMessage = 'Deseja remover este item da lista de sugestões?',
}) => {
  const [filterQuery, setFilterQuery] = useState('');
  const [confirmItem, setConfirmItem] = useState(null); // item to delete with confirmation
  const [newColor, setNewColor] = useState(defaultColor);

  // Edit Mode state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState(defaultColor);

  const filteredItems = items.filter(it => 
    !filterQuery.trim() || it.name.toLowerCase().includes(filterQuery.toLowerCase().trim())
  );

  const handleRequestDelete = (it) => {
    setConfirmItem(it);
  };

  const handleConfirmDelete = () => {
    if (confirmItem) {
      onDelete(confirmItem.id);
      setConfirmItem(null);
    }
  };

  const handleStartEdit = (it) => {
    setEditingId(it.id);
    setEditName(it.name);
    setEditColor(it.color || defaultColor);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveEdit = (id) => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    if (onUpdate) {
      onUpdate(id, { name: trimmed, color: editColor });
    }
    setEditingId(null);
  };

  const handleAdd = () => {
    if (onAdd) {
      onAdd({ name: newValue, color: newColor });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              {title}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
              {description}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Quick Add Input with Color Picker */}
            <div className="flex items-center gap-2">
              <ColorPicker
                value={newColor}
                onChange={setNewColor}
                testIdPrefix={`${testIdPrefix}-new-color`}
              />
              <Input
                data-testid={`${testIdPrefix}-new-input`}
                value={newValue}
                onChange={(e) => onNewValueChange(e.target.value)}
                placeholder={placeholder}
                className="h-10 bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-sm rounded-xl flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
              />
              <Button
                data-testid={`${testIdPrefix}-add-button`}
                onClick={handleAdd}
                className="bg-blue-900 hover:bg-blue-800 text-white rounded-xl h-10 px-4 gap-1.5 font-semibold shadow-xs flex-shrink-0"
              >
                <FiPlus size={16} />
                Adicionar
              </Button>
            </div>

            {/* Optional Filter if more than 5 items */}
            {items.length > 5 && (
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <Input
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  placeholder="Filtrar lista..."
                  className="pl-8 h-8 text-xs bg-slate-50/60 dark:bg-slate-800/40 rounded-lg border-slate-200/80 dark:border-slate-700"
                />
              </div>
            )}

            {/* List with Color Badges, Edit (Pencil) and Trash Icons */}
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {filteredItems.length === 0 ? (
                <div className="text-center py-6 text-xs text-slate-400 italic">
                  {items.length === 0 ? 'Nenhum item cadastrado' : 'Nenhum item encontrado no filtro'}
                </div>
              ) : (
                filteredItems.map((it) => (
                  <div key={it.id}>
                    {editingId === it.id ? (
                      /* Inline Edit Mode */
                      <div className="flex items-center gap-2 bg-blue-50/70 dark:bg-blue-950/40 rounded-xl p-2 border border-blue-200 dark:border-blue-800 animate-in fade-in duration-150">
                        <ColorPicker
                          value={editColor}
                          onChange={setEditColor}
                          testIdPrefix={`${testIdPrefix}-edit-color-${it.id}`}
                        />
                        <Input
                          data-testid={`${testIdPrefix}-edit-input-${it.id}`}
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-9 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-sm rounded-lg flex-1"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveEdit(it.id);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              handleCancelEdit();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          size="icon"
                          data-testid={`${testIdPrefix}-save-${it.id}`}
                          onClick={() => handleSaveEdit(it.id)}
                          className="h-9 w-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex-shrink-0"
                          title="Salvar alterações"
                        >
                          <FiCheck size={16} />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          data-testid={`${testIdPrefix}-cancel-${it.id}`}
                          onClick={handleCancelEdit}
                          className="h-9 w-9 rounded-lg flex-shrink-0"
                          title="Cancelar"
                        >
                          <FiX size={16} />
                        </Button>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex items-center justify-between bg-slate-50/80 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl px-3.5 py-2.5 transition-colors border border-slate-100 dark:border-slate-700/50 group">
                        <div className="flex items-center gap-2.5 truncate mr-2">
                          <span
                            className="w-3.5 h-3.5 rounded-full border border-black/10 shadow-2xs flex-shrink-0"
                            style={{ backgroundColor: it.color || defaultColor }}
                            title={`Cor: ${it.color || defaultColor}`}
                          />
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                            {it.name}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 flex-shrink-0">
                          {/* Edit Pencil Icon Button */}
                          <button
                            type="button"
                            data-testid={`${testIdPrefix}-edit-${it.id}`}
                            onClick={() => handleStartEdit(it)}
                            title="Editar item"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
                          >
                            <FiEdit2 size={15} />
                          </button>

                          {/* Trash Icon Button */}
                          <button
                            type="button"
                            data-testid={`${testIdPrefix}-remove-${it.id}`}
                            onClick={() => handleRequestDelete(it)}
                            title="Excluir da lista de sugestões"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center text-[11px] text-slate-400">
              <span>{items.length} item(ns) cadastrado(s)</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="h-8 px-3 rounded-xl text-xs"
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Sub-Modal */}
      <Dialog open={Boolean(confirmItem)} onOpenChange={(v) => !v && setConfirmItem(null)}>
        <DialogContent className="sm:max-w-sm bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl p-6">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0">
                <FiAlertTriangle size={18} />
              </div>
              Confirmar Remoção
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600 dark:text-slate-300 pt-2 leading-relaxed">
              {confirmMessage}
              <br />
              <strong className="text-slate-900 dark:text-slate-100 block mt-1">"{confirmItem?.name}"</strong>
              <span className="text-[11px] text-slate-400 block mt-1.5">
                (Registros financeiros já cadastrados anteriormente com este nome não serão afetados).
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800 mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmItem(null)}
              className="rounded-xl h-9 px-3 text-xs"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl h-9 px-4 text-xs font-semibold shadow-xs"
            >
              Sim, Remover
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CrudListDialog;
